/**
 * GlydeText - Text Chat Client for GLYDE Unity
 *
 * Provides text-based chat interaction with GLYDE AI agents through the Unity API.
 * Supports the same authentication methods as GlydeVoice.
 * Extends BaseGlydeClient for shared authentication and event handling.
 * Supports multiple context types and context switching via ContextSwitchManager.
 *
 * @example
 * // Screening interview for existing application
 * const textChat = new GlydeText({
 *   publishableKey: 'pk_...',
 *   contextType: 'screening',
 *   contextId: 'application-uuid'
 * });
 *
 * @example
 * // Discovery mode - unknown candidate exploring opportunities
 * const textChat = new GlydeText({
 *   publishableKey: 'pk_...',
 *   contextType: 'discovery'
 * });
 *
 * @example
 * // Legacy usage (defaults to discovery)
 * const textChat = new GlydeText({
 *   publishableKey: 'pk_...',
 *   contextId: 'client-uuid'
 * });
 *
 * const response = await textChat.sendMessage('Hello!');
 */

import type { GlydeTextConfig, TextChatEvent, ChatMessage, VoiceContextType, ContextSwitchPayload, AgentMetadata, VoiceConfig } from './types';
import { BaseGlydeClient } from './BaseGlydeClient';
import { ContextSwitchManager, type ContextSwitchCallbacks } from './ContextSwitchManager';

/**
 * Conversation summary from Unity API
 */
export interface ConversationSummaryResponse {
  application_uuid: string;
  has_history: boolean;
  message_count: number;
  last_message_date: string | null;
  summary_text: string;
  greeting: string;
}

/**
 * Chat response from Unity API (may include context_switch)
 */
interface TextChatResponse {
  history?: { messages: ChatMessage[] };
  response?: string;
  context_switch?: ContextSwitchPayload;
}

/**
 * GlydeText - Text Chat Client
 *
 * Connects to GLYDE Unity API for text-based conversations.
 * Supports multiple context types (screening, discovery, job_apply, etc.).
 * Uses ContextSwitchManager for context switching support.
 * Extends BaseGlydeClient for shared authentication and configuration.
 *
 * Note: By default, initializes with a summary instead of loading full history.
 * The AI greeting will acknowledge previous conversations when resuming.
 */
export class GlydeText extends BaseGlydeClient<GlydeTextConfig, TextChatEvent> {
  private history: ChatMessage[] = [];
  private isTyping = false;
  private conversationSummary: ConversationSummaryResponse | null = null;
  private contextSwitchManager: ContextSwitchManager;
  private currentContextType: VoiceContextType;
  private agentMetadata: AgentMetadata | null = null;
  private initPromise: Promise<ChatMessage[]> | null = null;

  /**
   * Create a new GlydeText instance
   * @param config - Configuration options
   */
  constructor(config: GlydeTextConfig) {
    super(config);

    // Default to discovery when contextType is not explicitly provided.
    this.currentContextType = config.contextType || 'discovery';

    // Initialize context switch manager
    this.contextSwitchManager = new ContextSwitchManager({
      fallbackTimeoutMs: 30000,
      onContextSwitchRequested: (payload) => {
        this.emitEvent({ type: 'context_switch', payload });
        if (this.config.onContextSwitch) {
          this.config.onContextSwitch(payload);
        }
      },
      onContextSwitchApplied: (contextType, contextId) => {
        console.log('[GlydeText] Context switch applied:', contextType, contextId);
      },
      onContextSwitchFailed: (error) => {
        console.error('[GlydeText] Context switch failed:', error);
        this.emitEvent({ type: 'error', payload: error });
      }
    });

    // Set up context switch callbacks
    const callbacks: ContextSwitchCallbacks = {
      performSmoothUpdate: this.performSmoothContextUpdate.bind(this),
      performFullReinit: this.performFullContextReinit.bind(this),
      canSmoothUpdate: () => true // Text chat can always do smooth updates
    };
    this.contextSwitchManager.setCallbacks(callbacks);

    // Log the Unity URL being used for debugging
    console.log('[GlydeText] Initialized with Unity API URL:', this.unityUrl, 'context:', this.currentContextType);
  }

  /**
   * Emit event to callback (implements BaseGlydeClient abstract method)
   * @param event - Text chat event to emit
   */
  protected emitEvent(event: TextChatEvent): void {
    if (this.config.onEvent) {
      this.config.onEvent(event);
    }
  }


  /**
   * Set typing state and notify callback
   */
  private setTyping(isTyping: boolean): void {
    this.isTyping = isTyping;
    if (this.config.onTyping) {
      this.config.onTyping(isTyping);
    }
  }

  /**
   * Initialize the chat session.
   * For screening context, fetches summary with greeting.
   * For other contexts (discovery, job_apply, etc.), generates initial greeting.
   *
   * @returns Array with the greeting message
   */
  async initialize(): Promise<ChatMessage[]> {
    // Return the existing history if already initialized
    if (this.initialized) {
      return this.history;
    }

    // If initialization is already in flight, return the same promise
    // to prevent concurrent inits from creating duplicate greetings
    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = this.doInitialize();
    try {
      return await this.initPromise;
    } finally {
      this.initPromise = null;
    }
  }

  /**
   * Internal initialization logic (called exactly once)
   */
  private async doInitialize(): Promise<ChatMessage[]> {
    this.setTyping(true);

    try {
      await this.fetchMetadata();

      if (this.currentContextType === 'screening') {
        if (!this.config.contextId) {
          throw new Error('contextId is required for screening context');
        }

        const summary = await this.unityFetch<ConversationSummaryResponse>(
          `/api/unity/screening/summary/${this.config.contextId}`,
          { method: 'GET' }
        );
        this.conversationSummary = summary;

        const greetingMessage: ChatMessage = {
          role: 'assistant',
          content: summary.greeting,
          created: new Date().toISOString()
        };

        this.history = [greetingMessage];
      } else {
        const greeting = this.agentMetadata?.greeting || this.getContextGreeting();
        const greetingMessage: ChatMessage = {
          role: 'assistant',
          content: greeting,
          created: new Date().toISOString()
        };

        this.history = [greetingMessage];
      }

      this.initialized = true;
      this.emitEvent({ type: 'ready' });
      this.emitEvent({ type: 'history_loaded', payload: this.history });

      return this.history;

    } catch (error) {
      console.error('[GlydeText] Initialization error:', error);
      this.emitEvent({ type: 'error', payload: error });
      throw error;

    } finally {
      this.setTyping(false);
    }
  }

  /**
   * Get a context-appropriate greeting for non-screening contexts
   * @returns Greeting message based on context type
   */
  private getContextGreeting(): string {
    switch (this.currentContextType) {
      case 'discovery':
        return "Hi there! I'm here to help you explore career opportunities. What kind of roles are you interested in?";
      case 'job_apply':
        return "Hi! I'm here to help you apply for this position. Would you like me to tell you more about the role?";
      case 'candidate_discover':
        return "Welcome back! I'm here to help you explore new opportunities. What are you looking for today?";
      case 'candidate_apply':
        return "Great to see you again! I can help you apply for this position. Shall we get started?";
      case 'recruiter':
        return "Hello! I'm your recruiting assistant. How can I help you today?";
      default:
        return "Hello! How can I assist you today?";
    }
  }

  /**
   * Get the conversation summary (available after initialize)
   */
  getSummary(): ConversationSummaryResponse | null {
    return this.conversationSummary;
  }

  /**
   * Send a message and get the agent's response.
   *
   * Uses incremental history: the user message and AI response are appended
   * to the local history array rather than replacing it with the full server
   * history.  This prevents old messages from prior sessions flooding the UI
   * and avoids duplicate user messages caused by optimistic-add + full-replace
   * race conditions.
   *
   * @param message - The message content to send
   * @returns The agent's response message (assistant role)
   */
  async sendMessage(message: string): Promise<ChatMessage> {
    if (this.requiresContextId() && !this.config.contextId) {
      throw new Error(`contextId is required for ${this.currentContextType} context`);
    }

    if (!message.trim()) {
      throw new Error('Message cannot be empty');
    }

    // Auto-initialize if not done
    if (!this.initialized) {
      await this.initialize();
    }

    // Add user message to local history immediately
    const userMessage: ChatMessage = {
      role: 'user',
      content: message.trim(),
      created: new Date().toISOString()
    };

    this.history.push(userMessage);

    if (this.config.onMessage) {
      this.config.onMessage(userMessage);
    }
    this.emitEvent({ type: 'message', payload: userMessage });

    this.setTyping(true);

    try {
      const requestBody = this.buildChatRequestBody(message.trim());
      const endpoint = this.getChatEndpoint();

      const data = await this.unityFetch<TextChatResponse>(endpoint, {
        method: 'POST',
        body: JSON.stringify(requestBody)
      });

      // Build the assistant response from the server reply.
      // Prefer the direct `response` field; fall back to extracting the last
      // assistant message from the returned history (if any).
      let assistantMessage: ChatMessage | null = null;

      if (data.response) {
        assistantMessage = {
          role: 'assistant',
          content: data.response,
          created: new Date().toISOString()
        };
      } else if (data.history?.messages) {
        const serverMsgs = data.history.messages.filter(
          (m: ChatMessage) => m.role !== 'system'
        );
        const lastAssistant = [...serverMsgs].reverse().find(m => m.role === 'assistant');
        if (lastAssistant) {
          assistantMessage = {
            role: 'assistant',
            content: lastAssistant.content,
            created: lastAssistant.created || new Date().toISOString()
          };
        }
      }

      if (!assistantMessage) {
        assistantMessage = {
          role: 'assistant',
          content: 'I received your message.',
          created: new Date().toISOString()
        };
      }

      // Append only the new AI response (incremental, not full history replace)
      this.history.push(assistantMessage);

      // Handle context switch if the server signalled one
      if (data.context_switch) {
        this.contextSwitchManager.requestContextSwitch(data.context_switch);
        await this.contextSwitchManager.flushPendingContextSwitch();
      }

      if (this.config.onMessage) {
        this.config.onMessage(assistantMessage);
      }
      this.emitEvent({ type: 'message', payload: assistantMessage });

      return assistantMessage;

    } catch (error) {
      console.error('[GlydeText] Send message error:', error);
      this.emitEvent({ type: 'error', payload: error });
      throw error;

    } finally {
      this.setTyping(false);
    }
  }

  /**
   * Check if current context type requires a context ID
   */
  private requiresContextId(): boolean {
    switch (this.currentContextType) {
      case 'screening':
      case 'job_apply':
      case 'candidate_discover':
      case 'candidate_apply':
        return true;
      case 'discovery':
      case 'recruiter':
      case 'custom':
      case 'phone':
      default:
        return false;
    }
  }

  /**
   * Get the appropriate chat endpoint based on context type
   */
  private getChatEndpoint(): string {
    // For now, all contexts use the screening endpoint
    // In the future, this could route to different endpoints based on context
    return '/api/unity/screening/chat';
  }

  /**
   * Build the chat request body based on context type
   * @param message - User message content
   */
  private buildChatRequestBody(message: string): Record<string, unknown> {
    const body: Record<string, unknown> = {
      message,
      context_type: this.currentContextType
    };

    // Add context ID based on context type
    if (this.config.contextId) {
      switch (this.currentContextType) {
        case 'screening':
          body.application_uuid = this.config.contextId;
          break;
        case 'job_apply':
          body.job_uuid = this.config.contextId;
          break;
        case 'candidate_discover':
          body.candidate_uuid = this.config.contextId;
          break;
        case 'candidate_apply':
          // contextId format: "candidate_uuid:job_uuid"
          const [candidateUuid, jobUuid] = this.config.contextId.split(':');
          body.candidate_uuid = candidateUuid;
          body.job_uuid = jobUuid;
          break;
        default:
          body.context_id = this.config.contextId;
      }
    }

    return body;
  }

  /**
   * Get the current conversation history
   *
   * IMPORTANT: System messages are filtered out - they contain AI context
   * (summaries, instructions) that should never be displayed to users.
   * Only user and assistant messages are returned.
   *
   * @returns Array of chat messages (user and assistant only)
   */
  getHistory(): ChatMessage[] {
    // Defensive filter to ensure system messages never leak to UI
    return [...this.history].filter(m => m.role !== 'system');
  }

  /**
   * Clear the local conversation history
   * Note: This does not clear server-side history
   */
  clearHistory(): void {
    this.history = [];
    this.initialized = false;
  }

  /**
   * Check if the chat is currently waiting for a response
   */
  getIsTyping(): boolean {
    return this.isTyping;
  }

  /**
   * Check if the chat has been initialized
   * @returns True if initialized
   */
  getIsInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Get the current context type
   */
  getContextType(): VoiceContextType {
    return this.currentContextType;
  }

  /**
   * Fetch agent metadata from the voice config endpoint.
   * Populates `agentMetadata` with agent name, company, job title, and greeting.
   * Called during initialize(); failures are non-fatal.
   */
  async fetchMetadata(): Promise<AgentMetadata | null> {
    try {
      const configPath = this.config.contextId
        ? `/api/unity/voice/config/${this.currentContextType}/${this.config.contextId}`
        : `/api/unity/voice/config/${this.currentContextType}`;

      const voiceConfig = await this.unityFetch<{ data: VoiceConfig }>(configPath, { method: 'GET' });
      const config = voiceConfig?.data ?? (voiceConfig as unknown as VoiceConfig);

      if (config?.metadata) {
        this.agentMetadata = config.metadata;
      }

      return this.agentMetadata;
    } catch (error) {
      console.warn('[GlydeText] Failed to fetch agent metadata (non-fatal):', error);
      return null;
    }
  }

  /**
   * Get the agent metadata (available after initialize or fetchMetadata)
   */
  getAgentMetadata(): AgentMetadata | null {
    return this.agentMetadata;
  }

  /**
   * Perform smooth context update (no full reinit)
   * For text chat, this just updates internal state
   * @param contextType - New context type
   * @param contextId - New context ID
   */
  private async performSmoothContextUpdate(contextType: VoiceContextType, contextId: string): Promise<void> {
    console.log('[GlydeText] Performing smooth context update:', contextType, contextId);

    // Update internal state
    this.currentContextType = contextType;
    this.config.contextId = contextId;
    this.updateContext({ contextType, contextId });

    // Clear history for new context
    this.history = [];
    this.conversationSummary = null;
    this.initialized = false;

    // Re-initialize with new context
    await this.initialize();
  }

  /**
   * Perform full context reinitialize
   * For text chat, this is the same as smooth update
   * @param contextType - New context type
   * @param contextId - New context ID
   */
  private async performFullContextReinit(contextType: VoiceContextType, contextId: string): Promise<void> {
    console.log('[GlydeText] Performing full context reinit:', contextType, contextId);

    // Same as smooth update for text chat
    await this.performSmoothContextUpdate(contextType, contextId);
  }

  /**
   * Manually switch to a new context
   * @param contextType - New context type
   * @param contextId - New context ID
   */
  async switchContext(contextType: VoiceContextType, contextId: string): Promise<void> {
    await this.performSmoothContextUpdate(contextType, contextId);
  }

  /**
   * Clean up resources when done
   */
  destroy(): void {
    this.contextSwitchManager.destroy();
    this.history = [];
    this.conversationSummary = null;
    this.initialized = false;
  }
}
